/**
 * Two-sided bracket geometry (owner 2026-10-02, TeamSnap-style redesign).
 *
 * CANONICAL SHARED COPY (INT-0037, 2026-10-07): consumed by the admin's
 * BracketTree so the admin scheduler renders the SAME shape as the public
 * bracket. apps/app and apps/next-app still carry byte-identical local
 * copies (apps/app/app/utils/bracketLayout.ts, apps/next-app/utils/…) —
 * repointing them here is a follow-up gated on the next-app parity suite
 * (any apps/app edit requires the mirror + parity run per CLAUDE.md). Until
 * then: EDIT ALL COPIES TOGETHER.
 *
 * Partitions the topology slots (from getBracketSlots) into the columns the
 * public PlayoffBracket renders LEFT→RIGHT on screen:
 *
 *   [5th-place col (+7th below)] [consolation-semis]  →  <QF CENTER>  →
 *       [semifinals] [final col (+3rd below)]  →  "Champion"
 *
 * Winners flow RIGHT to the Champion; the consolation/losers side flows LEFT
 * to 5th place. QFs are the shared center. Pure + framework-free so the
 * geometry is unit-tested without React. Feeder wiring itself lives in
 * bracketTopology (winnerTo/loserTo); this only decides column placement.
 */

export interface BracketSlotLike {
  key: string;
  label: string;
  stage?: string;
  playoffRound?: string;
  seeds?: number[];
}

export interface BracketColumn {
  title: string;
  slots: BracketSlotLike[];
  /** A placement match shown BELOW the column (3rd place under Final, 7th
   * under 5th) — the bottom-corner geometry from the TeamSnap reference. */
  corner?: BracketSlotLike | null;
}

export interface BracketLayout {
  centerTitle: string;
  center: BracketSlotLike[];
  /** Placement match under the CENTER column (3rd place under a lone
   * Championship — the semifinal-less layout, INT-0031). */
  centerCorner: BracketSlotLike | null;
  /** Screen order left→right (inner round first). */
  rightColumns: BracketColumn[];
  /** Screen order left→right (OUTERmost round first). */
  leftColumns: BracketColumn[];
  /** Endpoint label after the rightmost column (null for a lone final). */
  championLabel: string | null;
}

export function buildBracketLayout(slots: BracketSlotLike[]): BracketLayout {
  const byStage = (stage: string) => slots.filter((s) => s.stage === stage);
  const byKey = (key: string) => slots.find((s) => s.key === key) || null;

  const quarters = byStage('quarterfinal');
  const semis = byStage('semifinal');
  const consSemis = byStage('consolation_semifinal');
  const championship = byKey('championship');
  const third = byKey('third_place');
  const fifth = byKey('fifth_place');
  const seventh = byKey('seventh_place');

  const rightColumns: BracketColumn[] = [];
  const leftColumns: BracketColumn[] = [];
  let center: BracketSlotLike[];
  let centerTitle: string;
  let centerCorner: BracketSlotLike | null = null;

  // Consolation side (left), OUTERmost first: 5th-place col then cons-semis.
  // Shared by the QF-center and semis-center layouts — with the QF round
  // hidden (gameless, INT-0031) the consolation side must survive.
  const pushConsolation = () => {
    // 5th place stands alone when its feeder round was hidden as gameless.
    if (fifth) leftColumns.push({ title: '5th Place', slots: [fifth], corner: seventh });
    if (consSemis.length) leftColumns.push({ title: 'Consolation Semifinals', slots: consSemis });
  };

  if (quarters.length) {
    center = quarters;
    centerTitle = 'Quarterfinals';
    if (semis.length) rightColumns.push({ title: 'Semifinals', slots: semis });
    if (championship) rightColumns.push({ title: 'Championship', slots: [championship], corner: third });
    pushConsolation();
  } else if (semis.length) {
    center = semis;
    centerTitle = 'Semifinals';
    if (championship) rightColumns.push({ title: 'Championship', slots: [championship], corner: third });
    pushConsolation();
  } else {
    // Lone championship (2-team bracket, or earlier rounds hidden as
    // gameless) — 3rd place rides below the center column.
    center = championship ? [championship] : slots;
    centerTitle = championship ? 'Championship' : '';
    centerCorner = championship ? third : null;
    pushConsolation();
  }

  return {
    centerTitle,
    center,
    centerCorner,
    rightColumns,
    leftColumns,
    championLabel: championship ? 'Champion' : null,
  };
}

// Round-stage visibility (INT-0031, Nikkei organizer item #1): a round whose
// games were never created (pool play seeds the final directly; QFs skipped
// on an under-filled 8-team) must not render a column of TBD placeholders —
// but a genuinely UPCOMING round (no games anywhere later either) keeps its
// placeholders. Rule: drop a round's slots when the round has NO games and a
// LATER round on the SAME side (championship side vs consolation side) has at
// least one. Endpoints (championship/3rd, 5th/7th) are never dropped here —
// slot-game matching stays the caller's job; this only filters display slots.
const LATER_STAGES: Record<string, string[]> = {
  quarterfinal: ['semifinal', 'championship', 'third_place'],
  semifinal: ['championship', 'third_place'],
  consolation_semifinal: ['fifth_place', 'seventh_place'],
};

export function hideGamelessEarlierRounds<T extends BracketSlotLike>(
  slots: T[],
  hasGame: (slot: T) => boolean,
): T[] {
  const stageHasGame = (stage: string) => slots.some((s) => s.stage === stage && hasGame(s));
  const dropped = Object.keys(LATER_STAGES).filter((stage) => (
    slots.some((s) => s.stage === stage)
    && !stageHasGame(stage)
    && LATER_STAGES[stage].some(stageHasGame)
  ));
  if (!dropped.length) return slots;
  const droppedSet = new Set(dropped);
  return slots.filter((s) => !droppedSet.has(String(s.stage)));
}
